import { useMutation } from '@apollo/client';
import CustodianProfileForm from 'components/custodians/CustodianProfileForm';
import LayoutWithQuery from 'components/layout/LayoutWithQuery.tsx';
import { useSessionContext } from 'contexts/session-context';
import { loader } from 'graphql.macro';
import { useTracking } from 'lib/analytics/Tracker';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import MainLayout from 'components/layout/MainLayout';
import { makeStyles } from '@material-ui/core/styles';
import ENV from '../constants/Env';

const PROFILE_QUERY = loader('../data/queries/ProfileQuery.graphql');
const UPDATE_CUSTODIAN_MUTATION = loader(
  '../data/mutations/UpdateCustodianMutation.graphql',
);

const useStyles = makeStyles(theme => ({
  iframe: {
    minHeight: '100%',
    width: '100%',
    border: 'none',
  },
}));

function CustodianProfileContainer() {
  const [updateError, setUpdateError] = useState(null);
  const { session } = useSessionContext();
  const tracker = useTracking();
  const history = useHistory();
  const classes = useStyles();

  // Feature flag to show the portal user settings screen
  const portalUserSettings = session?.featureFlags?.includes(
    'dashboard_portal_user_settings',
  );

  useEffect(() => {
    tracker.track('Coordinator Profile Viewed');
  }, [tracker]);

  useEffect(() => {
    window.addEventListener(
      'message',
      event => {
        if (event.origin !== ENV.portalUrl) {
          return;
        }

        const { data } = event;

        if (data.action === 'USER_UPDATED') {
          history.push('/');
        }
      },
      false,
    );
  }, []);

  const onCustodianUpdated = data => {
    const error = data.updateCustodian.error;

    if (error) {
      onCustodianUpdatedError(error);
    } else {
      history.goBack();
    }
  };

  const onCustodianUpdatedError = error => {
    if (error) {
      setUpdateError(error);
    }
  };

  const [updateCustodian] = useMutation(UPDATE_CUSTODIAN_MUTATION, {
    refetchQueries: [{ query: PROFILE_QUERY }],
    onCompleted: onCustodianUpdated,
    onError: onCustodianUpdatedError,
  });

  const custodianToPersist = data => {
    const { phone, notifPrefs, firstName, lastName, email, password } = data;
    return {
      phone,
      notifPrefs,
      firstName,
      lastName,
      email,
      password,
    };
  };

  const onUpdate = data => {
    tracker.track('Custodian Profile Updated');
    // alert(JSON.stringify(riderToPersist(data), null, 2));

    updateCustodian({ variables: { custodian: custodianToPersist(data) } });
  };

  if (portalUserSettings) {
    /* If the user has the feature flag, we show the portal user settings
     * screen instead of the profile form.
     * The `hideNav=true` query param tells Portal to not show the side
     * navigation bar.
     */
    return (
      <MainLayout usePadding="no">
        <iframe
          data-test-id="portal-iframe"
          title="User Settings Screen"
          src={`${ENV.portalUrl}/user-settings?hideNav=true`}
          className={classes.iframe}
        />
      </MainLayout>
    );
  } else {
    /* If the user does not have the feature flag, we show the profile form
     * instead of the portal user settings screen.
     */
    return (
      <LayoutWithQuery
        query={PROFILE_QUERY}
        renderView={data => (
          <CustodianProfileForm
            custodian={data.profile}
            updateError={updateError}
            onUpdate={onUpdate}
          />
        )}
      />
    );
  }
}

export default CustodianProfileContainer;
